import { InfoIcon } from "@chakra-ui/icons";
import { ButtonProps, Flex, Text } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { ProjectRole } from "@nens/rana/api-v1";
import { RanaModal, RanaTable, RightArrowIcon, SendIcon } from "@nens/ranalib";
import { RanaErrors } from "@nens/ranalib";

import { TableEmptyUserOrInvite } from "@/components/table-empty-user-or-invite";
import { Profile } from "@/lib/profile";
import { cancelButtonText } from "@/translations";
import { getCompleteName } from "@/utils/get-complete-name";

import { useProjectUserInviteModal } from "../hooks/use-project-user-invite-modal";

import { ProjectUserRowWithRoleSelection } from "./project-user-row-editable";

interface ProjectUserInviteModalProps {
  onClose: () => void;
}

export function ProjectUserInviteModal({ onClose }: ProjectUserInviteModalProps) {
  const intl = useIntl();
  const {
    searchString,
    setSearchString,
    foundUsers,
    addUsers,
    showInviteButton,
    addUsersErrors,
    inviteUsersErrors,
    isLoading,
    inviteUser,
    showFinalStep,
    setShowFinalStep,
    selectedUsers,
    setSelectedUsers,
    updateUserRole,
    allUsersAreCollaborator,
    paginationProps,
  } = useProjectUserInviteModal(onClose);

  const inviteButton: ButtonProps = (() => {
    if (showInviteButton) {
      return {
        children: intl.formatMessage({
          id: "button.invite_user",
          defaultMessage: "Invite user",
        }),
        variant: "primary",
        onClick: inviteUser,
        rightIcon: <SendIcon />,
      };
    }
    if (showFinalStep) {
      return {
        children: intl.formatMessage({
          id: "button.send_invitation",
          defaultMessage: "Send invitation",
        }),
        variant: "primary",
        onClick: addUsers,
        rightIcon: <SendIcon />,
        isDisabled: isLoading || selectedUsers.length === 0,
      };
    }
    return {
      children: intl.formatMessage({
        id: "button.overview",
        defaultMessage: "Overview",
      }),
      variant: "primary",
      onClick: () => setShowFinalStep(true),
      rightIcon: <RightArrowIcon />,
      isDisabled: isLoading || selectedUsers.length === 0,
    };
  })();

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={
        <FormattedMessage
          id="header.invite_user_to_project"
          defaultMessage="Invite users to project"
        />
      }
      w="2xl"
      h="3xl"
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          isDisabled: isLoading,
          onClick: onClose,
        },
        inviteButton,
      ]}
      bottomChild={
        selectedUsers.length > 0 && (
          <Flex
            borderWidth="base"
            borderColor="brand.analytic"
            bgColor="semantic.info.background"
            p={4}
            borderRadius="base"
            gap={2}
            display={showFinalStep && allUsersAreCollaborator ? "flex" : "none"}
          >
            <InfoIcon color="brand.analytic" boxSize={6} />
            <Text textStyle="md" color="brand.deep_sea" fontWeight={300}>
              {showFinalStep ? (
                <FormattedMessage
                  id="info.all_users_are_collaborators"
                  defaultMessage="All users are collaborators"
                />
              ) : (
                <FormattedMessage
                  id="info.selected_users"
                  defaultMessage="You have selected {count} users."
                  values={{
                    count: (
                      <Text color="brand.analytic" as="strong">
                        {selectedUsers.length}
                      </Text>
                    ),
                  }}
                />
              )}
            </Text>
          </Flex>
        )
      }
    >
      {showFinalStep ? (
        // Final screen for adding users to the project
        <Flex flexDir="column" gap={2} color="brand.deep_sea">
          <Text textStyle="xs" textTransform="uppercase" color="brand.analytic" fontWeight={600}>
            <FormattedMessage
              id="info.select_project_role"
              defaultMessage="Select project role of users"
            />
          </Text>
          {selectedUsers.map(({ user, role }) => (
            <ProjectUserRowWithRoleSelection
              key={user.id}
              user={user}
              role={role}
              updateUserRole={updateUserRole}
            />
          ))}
        </Flex>
      ) : (
        // Table of all found users
        <RanaTable
          hasRowActions
          columnDefinitions={[
            {
              width: "table_cell_xxxs",
            },
            {
              width: "table_cell_xs",
            },
            {
              width: "table_cell_sm",
            },
          ]}
          tableActions={[
            {
              label: "",
              action: () => null,
            },
          ]}
          tableDataIds={foundUsers.map((user) => user.id)}
          emptyElement={<TableEmptyUserOrInvite />}
          isSkeleton={isLoading}
          onCheckboxClicked={(rowIds) =>
            setSelectedUsers(
              foundUsers
                .filter((user) => rowIds.includes(user.id))
                .map((user) => ({ user, role: ProjectRole.Collaborator }))
            )
          }
        >
          <RanaTable.Filters
            search={{
              placeholder: intl.formatMessage({
                id: "input.name_or_email_address",
                defaultMessage: "Enter name or email address",
              }),
              searchString,
              setSearchString,
              w: "full",
            }}
          />
          <RanaTable.Container mt={4} minH={0} maxH="sm" overflowY="auto">
            <RanaTable.Body>
              {foundUsers.map((user) => (
                <RanaTable.Row key={user.id} rowId={user.id} h={16}>
                  <RanaTable.Cell>
                    <Profile inTable user={user} />
                  </RanaTable.Cell>
                  <RanaTable.Cell whiteSpace="normal">
                    <Text fontWeight={600} color="brand.analytic">
                      {getCompleteName(user)}
                    </Text>
                  </RanaTable.Cell>
                  <RanaTable.Cell>{user.email}</RanaTable.Cell>
                </RanaTable.Row>
              ))}
            </RanaTable.Body>
          </RanaTable.Container>
          <RanaTable.Pagination {...paginationProps} />
        </RanaTable>
      )}
      <RanaErrors errors={addUsersErrors} />
      <RanaErrors errors={inviteUsersErrors} />
    </RanaModal>
  );
}
